/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useStoreDispatch } from "../../../store/hooks";
import { setBasicDialogOpenFor } from "../../../store/helpers/helperReducers";
import { IZoneDevice } from "../../../store/api/deviceConfig/types";
import { useZoneDeviceData } from "./hooks";
import { RadixSelectControl } from "../../ui/radix-select";
import { getProtocolList } from "../../../pages/Administrations/helpers";
import { ZoneTypeList } from "../../../globals/constants";
import { isError } from "../../../utils/validations";
import { Sheet } from "../../ui/sheet";
import { Button } from "../../ui/button";
import { InputField } from "../../Shared/Radix/Inputs/InputField";
import { Label } from "../../ui/label";
import { Loader2 } from "lucide-react";

interface IAddZoneCellProps {
  selectedRow: IZoneDevice;
  modelOpenMode?: string;
  handleClear: () => void;
}

const AddZoneCell: React.FC<IAddZoneCellProps> = ({ modelOpenMode = "Add", selectedRow, handleClear }) => {
  const dispatch = useStoreDispatch();
  const { zoneDeviceData, setZoneDeviceData, submitHandler, onBlurHandler, errMsg, setErrMsg, selectedDevice, configStatus } =
    useZoneDeviceData(selectedRow);

  const handleChange = (name: string, value: string) => {
    setZoneDeviceData({ ...zoneDeviceData, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
  };

  const handleClose = () => {
    dispatch(setBasicDialogOpenFor(undefined));
  };

  const { index, priority, dlBandwidth, ulBandwidth, eARFCNDL, eARFCNUL } = zoneDeviceData;
  const checkNegativeVal = [Number(index), Number(dlBandwidth), Number(ulBandwidth), Number(eARFCNDL), Number(eARFCNUL)].every(val => val > 0);
  const isDiabledSave = !(!!index && !!priority && !!dlBandwidth && !!ulBandwidth && !!eARFCNDL && !!eARFCNUL) || isError(errMsg) || !checkNegativeVal;

  const footer = (
    <>
      <Button variant="outline" onClick={handleClose} disabled={configStatus.isLoading}>
        Cancel
      </Button>
      <Button
        type="submit"
        form="add-zone-form"
        disabled={configStatus.isLoading || isDiabledSave}
      >
        {configStatus.isLoading && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
        Save
      </Button>
    </>
  );

  const isAdd = modelOpenMode?.toLowerCase() === "add";

  return (
    <Sheet
      open={true}
      onClose={handleClose}
      title={isAdd ? "Add New Zone" : `Update Zone ${zoneDeviceData?.index}`}
      footer={footer}
      width="520px"
    >
      <form
        id="add-zone-form"
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          submitHandler();
          modelOpenMode?.toLowerCase() !== "add" && handleClear();
        }}
      >
        <div className="space-y-5 pt-2">
          {/* ID */}
          <InputField
            label="ID"
            name="index"
            type="number"
            value={zoneDeviceData?.index ?? ""}
            onChange={handleChange}
            onBlur={onBlurHandler}
            disabled={!isAdd || configStatus.isLoading}
            min={1}
            max={255}
            placeholder="Enter ID"
            required
          />

          {/* Zone Type */}
          {selectedDevice?.nodeType === "SN" && isAdd && (
            <div className="space-y-2">
              <Label htmlFor="zone-zoneType">Zone Type</Label>
              <RadixSelectControl
                options={getProtocolList(ZoneTypeList).map(opt => ({ value: opt.value, label: opt.text }))}
                value={zoneDeviceData?.zoneType || ""}
                onValueChange={(value) => {
                  handleChange("zoneType", value);
                }}
                placeholder="Select zone type"
                disabled={configStatus.isLoading}
              />
            </div>
          )}

          {/* Priority */}
          <InputField
            label="Priority"
            name="priority"
            type="number"
            value={zoneDeviceData?.priority ?? ""}
            onChange={handleChange}
            onBlur={onBlurHandler}
            disabled={!isAdd || configStatus.isLoading}
            min={1}
            max={127}
            required
            placeholder="Enter priority"
          />

          {/* Description */}
          <InputField
            label="Description"
            name="description"
            value={zoneDeviceData?.description ?? ""}
            onChange={handleChange}
            onBlur={onBlurHandler}
            disabled={configStatus.isLoading}
            placeholder="Enter description"
          />

          {/* ARFCNUL */}
          <InputField
            label="ARFCNUL"
            name="eARFCNUL"
            type="number"
            value={zoneDeviceData?.eARFCNUL ?? ""}
            onChange={handleChange}
            onBlur={onBlurHandler}
            disabled={configStatus.isLoading}
            min={1}
            required
            placeholder="Enter ARFCNUL"
          />

          {/* UL bandwidth */}
          <InputField
            label="UL Bandwidth"
            name="ulBandwidth"
            type="number"
            value={zoneDeviceData?.ulBandwidth ?? ""}
            onChange={handleChange}
            onBlur={onBlurHandler}
            disabled={configStatus.isLoading}
            min={1}
            required
            placeholder="Enter UL bandwidth"
          />

          {/* ARFCNDL */}
          <InputField
            label="ARFCNDL"
            name="eARFCNDL"
            type="number"
            value={zoneDeviceData?.eARFCNDL ?? ""}
            onChange={handleChange}
            onBlur={onBlurHandler}
            disabled={configStatus.isLoading}
            min={1}
            required
            placeholder="Enter ARFCNDL"
          />

          {/* DL bandwidth */}
          <InputField
            label="DL Bandwidth"
            name="dlBandwidth"
            type="number"
            value={zoneDeviceData?.dlBandwidth ?? ""}
            onChange={handleChange}
            onBlur={onBlurHandler}
            disabled={configStatus.isLoading}
            min={1}
            required
            placeholder="Enter DL bandwidth"
          />
        </div>
      </form>
    </Sheet>
  );
};

export default AddZoneCell;
